import React from 'react';
import styled, { css } from 'styled-components';

export type TagVariant =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';
export type TagSize = 'sm' | 'md';

type StyledTagProps = {
  $variant: TagVariant;
  $size: TagSize;
};

const variantStyles: Record<TagVariant, ReturnType<typeof css>> = {
  neutral: css`
    background: var(--color-surface-subtle);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border);
  `,
  accent: css`
    background: var(--color-accent-subtle);
    color: var(--color-accent-text);
    border: 1px solid transparent;
  `,
  success: css`
    background: var(--color-success-subtle);
    color: var(--color-success);
    border: 1px solid transparent;
  `,
  warning: css`
    background: var(--color-warning-subtle);
    color: var(--color-warning);
    border: 1px solid transparent;
  `,
  danger: css`
    background: var(--color-danger-subtle);
    color: var(--color-danger);
    border: 1px solid transparent;
  `,
  info: css`
    background: var(--color-info-subtle);
    color: var(--color-info);
    border: 1px solid transparent;
  `,
};

const sizeStyles: Record<TagSize, ReturnType<typeof css>> = {
  sm: css`
    height: 20px;
    padding: 0 var(--space-2);
    font-size: var(--font-size-xs);
    gap: 4px;
  `,
  md: css`
    height: 24px;
    padding: 0 var(--space-3);
    font-size: var(--font-size-sm);
    gap: var(--space-1);
  `,
};

const StyledTag = styled.span<StyledTagProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  font-weight: var(--font-weight-medium);
  border-radius: var(--radius-pill);
  white-space: nowrap;
  line-height: 1;
  ${(p) => sizeStyles[p.$size]}
  ${(p) => variantStyles[p.$variant]}
`;

const Dot = styled.span<{ $variant: TagVariant }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
`;

export type TagProps = React.HTMLAttributes<HTMLSpanElement> & {
  variant?: TagVariant;
  size?: TagSize;
  withDot?: boolean;
};

export const Tag: React.FC<TagProps> = ({
  variant = 'neutral',
  size = 'md',
  withDot = false,
  children,
  ...rest
}) => {
  return (
    <StyledTag $variant={variant} $size={size} {...rest}>
      {withDot && <Dot $variant={variant} aria-hidden="true" />}
      {children}
    </StyledTag>
  );
};
